<template>
  <overlay-side-panel
    v-bind:show="show"
    v-bind:title="$t('appUserListPanelFilter.filter')"
    v-on="$listeners"
  >
    <template v-slot:title="{ title }">
      <div class="title-container">
        <h5 class="title">{{ title }}</h5>
        <a
          v-if="!isAllEmpty"
          class="link"
          v-t="'appUserListPanelFilter.action.clear'"
          v-on:click="$_onClickClear"
        />
      </div>
    </template>
    <div class="contents">
      <user-status-filter-keyword
        v-if="isAvailable.search"
        v-bind="localCondition?.search"
        v-model="currentKeyword"
        v-on:input:keyword="currentKeyword = $event"
        v-on:changed="$_onChanged('search', $event)"
      />
      <hr v-if="isBothSearchAndOthersAvailable" class="separator" />
      <user-status-filter-type
        v-if="isAvailable.userType"
        v-bind="localCondition?.type"
        v-on:changed="$_onChanged('userType', $event)"
      />
      <user-status-filter-organization
        v-if="isAvailable.organization"
        v-bind="localCondition?.organization"
        v-on:changed="$_onChanged('organization', $event)"
      />
      <user-status-filter-indoor
        v-if="isAvailable.indoor"
        v-bind="localCondition?.indoor"
        v-on:changed="$_onChanged('indoor', $event)"
      />
      <user-status-filter-place
        v-if="isAvailable.place"
        v-bind="{ ...localCondition?.place, ...localCondition?.indoor }"
        v-on:changed="$_onChanged('place', $event)"
      />
      <user-status-filter-status
        v-if="isAvailable.status"
        v-bind="localCondition?.status"
        v-on:changed="$_onChanged('status', $event)"
      />
      <user-status-filter-activity
        v-if="isAvailable.activity"
        v-bind="localCondition?.activity"
        v-on:changed="$_onChanged('activity', $event)"
      />
      <user-status-filter-positioning
        v-if="isAvailable.positioning"
        v-bind="localCondition?.positioning"
        v-on:changed="$_onChanged('positioning', $event)"
      />
    </div>
  </overlay-side-panel>
</template>

<script>
import OverlaySidePanel from '@/components/AppOverlaySidePanel.vue'
import {
  UserStatusFilterOrganization,
  UserStatusFilterIndoor,
  UserStatusFilterKeyword,
  UserStatusFilterPlace,
  UserStatusFilterStatus,
  UserStatusFilterActivity,
  UserStatusFilterPositioning,
  UserStatusFilterType,
} from '@/components/user/filteritem'
import {
  CONDITION_AVAILABLE_FUNCTION_TYPE,
  useUserListFilterCondition,
} from '@/composables/user/UseUserListFilterCondition'

export default {
  name: 'AppUserListPanelFilter',
  components: {
    OverlaySidePanel,
    UserStatusFilterOrganization,
    UserStatusFilterIndoor,
    UserStatusFilterKeyword,
    UserStatusFilterPlace,
    UserStatusFilterStatus,
    UserStatusFilterActivity,
    UserStatusFilterPositioning,
    UserStatusFilterType,
  },
  model: {
    prop: 'show',
    event: 'change:show',
  },
  props: {
    /**
     * パネル表示フラグ
     * @model
     */
    show: {
      type: Boolean,
      default: false,
    },
    /**
     * 絞り込み条件
     */
    condition: {
      type: Object,
      default: () => {},
    },
  },
  emits: [
    /**
     * 検索条件変更イベント
     * @param {Object} 検索条件
     */
    'change:condition',
    /**
     * 状態が全て空かどうかの変更イベント
     * @param {boolean} 状態が全て空の場合はtrue
     */
    'change:isAllEmpty',
    /**
     * パネル表示状態変更イベント(v-model更新用)
     * @param {boolean} パネル表示状態
     */
    'change:show',
  ],
  setup() {
    const { getBlankCondition } = useUserListFilterCondition()
    return { getBlankCondition }
  },
  data: function () {
    return {
      // ローカル保持用絞り込み条件
      localCondition: this.condition,
      // 入力中のキーワード
      currentKeyword: this.condition?.search?.keyword || null,
    }
  },
  computed: {
    /**
     * 各絞り込み条件の利用可能フラグ
     * @type {Object}
     */
    isAvailable: function () {
      return Object.fromEntries(
        [...Object.entries(CONDITION_AVAILABLE_FUNCTION_TYPE)].map(
          ([key, type]) => [key, !!this.$store.getters.functionType(type)]
        )
      )
    },
    /**
     * キーワード検索とその他の検索が両方とも利用可能な場合はtrue
     * @type {boolean}
     */
    isBothSearchAndOthersAvailable: function () {
      const availableTypes = Object.entries(this.isAvailable)
        .filter(([, value]) => value)
        .map(([type]) => type)
      return availableTypes.length >= 2 && availableTypes.includes('search')
    },
    /**
     * props.condition と localCondition が値レベルで変わっている場合はtrue
     * @type {Boolean}
     */
    isConditionValueDifferent: function () {
      return (
        JSON.stringify(this.condition) !== JSON.stringify(this.localCondition)
      )
    },
    /**
     * 検索条件が空の場合はtrue
     * @type {boolean}
     */
    isConditionEmpty: function () {
      return (
        JSON.stringify(this.localCondition) ===
        JSON.stringify(this.getBlankCondition())
      )
    },
    /**
     * 状態が全て空の場合はtrue
     * @type {Boolean}
     */
    isAllEmpty: function () {
      return this.isConditionEmpty && this.currentKeyword == null
    },
  },
  watch: {
    condition: {
      deep: true,
      handler: function (value) {
        this.localCondition = { ...value }
        this.currentKeyword = value?.search?.keyword || null
      },
    },
    isAllEmpty: function (value) {
      this.$emit('change:isAllEmpty', value)
    },
  },
  methods: {
    /**
     * クリアリンククリック時処理
     */
    $_onClickClear: function () {
      this.localCondition = this.getBlankCondition()
      this.currentKeyword = null
      // store にも保存
      this.$store.dispatch(
        'userKeywordTarget/setKeywordTargets',
        this.localCondition.search.keywordTargets
      )

      if (this.isConditionValueDifferent) {
        this.$emit('change:condition', this.localCondition)
      }
    },

    /**
     * 条件変更時処理
     * @param {String} type 絞り込み条件種類
     * @param {Object} result 変更結果
     */
    $_onChanged: function (type, result) {
      const { value } = result
      const newCondition = {
        ...this.localCondition,
        [type]: value,
      }
      // キーワード検索以外の変更の場合は画面上の表示と検索条件を合わせるため現在のキーワード入力値をキーワード条件に設定
      if (type !== 'search') {
        newCondition.search.keyword =
          this.currentKeyword === '' ? null : this.currentKeyword
      }
      this.localCondition = newCondition

      if (this.isConditionValueDifferent) {
        this.$emit('change:condition', this.localCondition)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../assets/scss/mixin' as mixin;

$-padding-horizontal: 12px;

/**
 * 配置関連スタイル
 */
.title-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 16px;
  line-height: 1;

  .title {
    font-size: $font-size-large;
    font-weight: $font-weight-bold;
    margin: 0;
  }

  .link {
    font-size: $font-size-middle;
    color: $text-color-link;
    text-decoration: $text-color-link underline;

    &:hover {
      cursor: pointer;
    }
  }
}

.contents {
  width: 100%;
  height: 100%;
  margin-top: 4px;
  padding: 0 8px 8px;
  display: grid;
  grid-template-columns: max-content max-content;
  grid-auto-rows: max-content;
  row-gap: 12px;
  column-gap: 12px;
  overflow-x: hidden;
  overflow-y: auto;
  // スクロールバー発生時に右側のpadding部に出すための設定
  width: calc(100% + $-padding-horizontal);
  margin-right: -$-padding-horizontal;
  @include mixin-scrollbar;
  & {
    scrollbar-gutter: stable;
  }

  ::v-deep .item-label {
    font-size: $font-size-middle;
  }

  ::v-deep .user-type {
    white-space: nowrap;
  }

  .separator {
    grid-column: 1 / 3;
    margin: 8px 0;
    border-top: 1px solid $border-color;
  }
}
</style>
